/**
 * Exit-intent popup — fires when the user's cursor leaves the viewport toward
 * the top of the page (desktop) or after 45 seconds of inactivity (mobile).
 * Shown at most once per session; dismissed state is stored in sessionStorage.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle, Loader2, Plane } from 'lucide-react';

const SESSION_KEY = 'exit_popup_dismissed';

interface ExitIntentPopupProps {
  language?: 'en' | 'es';
}

export default function ExitIntentPopup({ language = 'en' }: ExitIntentPopupProps) {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const triggered = useRef(false);
  const mobileTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const isEs = language === 'es';

  const copy = {
    eyebrow: isEs ? 'Antes de que te vayas…' : 'Before you go…',
    heading: isEs ? '¡Ofertas exclusivas te esperan!' : 'Exclusive deals are waiting for you!',
    body: isEs
      ? 'Únete a miles de viajeros que reciben destinos secretos y ofertas especiales antes que nadie.'
      : 'Join thousands of travelers who get secret destinations and special offers before anyone else.',
    placeholder: isEs ? 'Tu correo electrónico' : 'Your email address',
    cta: isEs ? 'Quiero las ofertas' : 'Send me the deals',
    noThanks: isEs ? 'No, gracias' : 'No thanks',
    success: isEs
      ? '¡Listo! Pronto recibirás inspiración de viaje.'
      : "You're in! Travel inspiration is on its way.",
    sending: isEs ? 'Enviando…' : 'Sending…',
    noSpam: isEs ? 'Sin spam. Cancela cuando quieras.' : 'No spam. Unsubscribe anytime.',
  };

  function dismiss() {
    setVisible(false);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
  }

  function trigger() {
    if (triggered.current) return;
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch { /* ignore */ }
    triggered.current = true;
    setVisible(true);
  }

  useEffect(() => {
    // Desktop: mouse leaves toward the top of the viewport
    function onMouseLeave(e: MouseEvent) {
      if (e.clientY <= 10) trigger();
    }

    // Mobile / fallback: 45 s inactivity timer
    mobileTimer.current = setTimeout(trigger, 45_000);

    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      if (mobileTimer.current) clearTimeout(mobileTimer.current);
    };
  }, []);

  // Focus management: move focus into modal when it opens; restore on close
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (visible) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Defer so the modal is in the DOM before we focus
      setTimeout(() => closeButtonRef.current?.focus(), 50);
    } else {
      previousFocusRef.current?.focus();
    }
  }, [visible]);

  // Escape key closes the modal; focus trap keeps Tab inside it
  useEffect(() => {
    if (!visible) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        dismiss();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language, source: 'exit-intent-popup' }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        setStatus('success');
        setEmail('');
        // Auto-close after 3 s on success
        setTimeout(dismiss, 3000);
      } else {
        setStatus('error');
        setErrorMsg(data.error ?? (isEs ? 'Algo salió mal.' : 'Something went wrong.'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(isEs ? 'Algo salió mal.' : 'Something went wrong.');
    }
  }

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={dismiss}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-popup-heading"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' as const }}
          >
            <div ref={modalRef} className="relative w-full max-w-md bg-[#0a1420] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">

              {/* Top accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-[#00E5FF] via-[#0065ff] to-[#00E5FF]" aria-hidden="true" />

              {/* Close button */}
              <button
                ref={closeButtonRef}
                onClick={dismiss}
                aria-label={isEs ? 'Cerrar' : 'Close'}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-10"
              >
                <X size={20} aria-hidden="true" />
              </button>

              <div className="px-8 py-8">
                {/* Icon */}
                <div className="flex justify-center mb-5">
                  <div className="w-14 h-14 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
                    <Plane size={24} className="text-[#00E5FF]" />
                  </div>
                </div>

                {/* Eyebrow */}
                <p className="text-[#00E5FF] text-xs font-semibold tracking-wide text-center mb-2 uppercase">
                  {copy.eyebrow}
                </p>

                {/* Heading */}
                <h2
                  id="exit-popup-heading"
                  className="text-white text-2xl font-bold text-center mb-3 leading-snug font-heading"
                >
                  {copy.heading}
                </h2>

                {/* Body */}
                <p className="text-white/55 text-sm text-center leading-relaxed mb-7">
                  {copy.body}
                </p>

                {/* Form / Success */}
                <AnimatePresence mode="wait">
                  {status === 'success' ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex flex-col items-center gap-3 py-2"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#00E5FF]/15 flex items-center justify-center">
                        <CheckCircle size={24} className="text-[#00E5FF]" />
                      </div>
                      <p className="text-white text-base font-semibold text-center">{copy.success}</p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-3"
                    >
                      <label htmlFor="exit-popup-email" className="sr-only">
                        {copy.placeholder}
                      </label>
                      <input
                        id="exit-popup-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={copy.placeholder}
                        required
                        className="w-full bg-white/8 border border-white/15 rounded-xl px-4 py-3.5 text-white placeholder-white/35 text-sm focus:outline-none focus:border-[#00E5FF]/60 transition-colors"
                      />

                      {status === 'error' && (
                        <p className="text-red-400 text-xs -mt-1">{errorMsg}</p>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full py-3.5 bg-[#00E5FF] text-[#0a1420] font-bold text-sm rounded-xl hover:bg-[#00c8e0] transition-all duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:scale-100 flex items-center justify-center gap-2"
                      >
                        {status === 'loading' ? (
                          <><Loader2 size={16} className="animate-spin" />{copy.sending}</>
                        ) : copy.cta}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>

                {/* No thanks */}
                {status !== 'success' && (
                  <div className="text-center mt-4">
                    <button
                      onClick={dismiss}
                      className="text-white/30 text-xs hover:text-white/60 transition-colors"
                    >
                      {copy.noThanks}
                    </button>
                    <p className="text-white/20 text-xs mt-1">{copy.noSpam}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
